import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import InlineMarkupEditor from '../components/InlineMarkupEditor.js';
import DemoAIAnalysisPanel from '../components/DemoAIAnalysisPanel.js';
import '../styles/ENEMTrainingPage.css';

const DemoENEMTrainingPage = () => {
  const { t } = useTranslation();
  
  // Essay form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [submissionType, setSubmissionType] = useState('text');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Analysis state
  const [inlineErrors, setInlineErrors] = useState([]);
  const [showMarkups, setShowMarkups] = useState(false);

  // Demo themes
  const themes = [
    {
      id: 1,
      year: 2023,
      title: "Desafios para o enfrentamento da invisibilidade do trabalho de cuidado realizado pela mulher no Brasil",
      description: "Analise os desafios relacionados ao reconhecimento e valorização do trabalho de cuidado feminino."
    },
    {
      id: 2,
      year: 2022,
      title: "Desafios para a valorização de comunidades e povos tradicionais no Brasil",
      description: "Discuta as dificuldades enfrentadas por comunidades tradicionais brasileiras."
    },
    {
      id: 3,
      year: 2021,
      title: "Invisibilidade e registro civil: garantia de acesso à cidadania no Brasil",
      description: "Aborde a importância do registro civil para o exercício da cidadania."
    },
    {
      id: 4,
      year: 2020,
      title: "O estigma associado às doenças mentais na sociedade brasileira",
      description: "Analise o preconceito relacionado às questões de saúde mental no Brasil."
    },
    {
      id: 5,
      year: 2019,
      title: "Democratização do acesso ao cinema no Brasil",
      description: "Discuta os desafios para tornar o cinema mais acessível à população brasileira."
    }
  ];

  // Demo essays for display
  const demoEssays = [
    {
      id: 1,
      title: "Redação sobre trabalho de cuidado",
      formattedDate: "15/01/2025",
      type: "text",
      wordCount: 450,
      hasAnalysis: true,
      analysis: { totalScore: 850 },
      scoreColor: "#4CAF50"
    },
    {
      id: 2,
      title: "Comunidades tradicionais no Brasil",
      formattedDate: "10/01/2025",
      type: "image",
      wordCount: 380,
      hasAnalysis: false
    }
  ];

  // Handle theme selection
  const handleThemeSelect = (theme) => {
    setSelectedTheme(theme);
    setTitle(theme.title);
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file must be smaller than 10MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      setImageFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  // Handle content change from editor
  const handleContentChange = useCallback((htmlValue, plainText) => {
    setContent(htmlValue);
  }, []);

  // Handle inline errors update from AI analysis
  const handleInlineErrorsUpdate = useCallback((errors) => {
    setInlineErrors(errors || []);
    if (errors && errors.length > 0) {
      setShowMarkups(true);
    }
  }, []);

  // Handle essay submission (demo mode)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (submissionType === 'text' && !content.trim()) {
      setError('Por favor, escreva o conteúdo da redação');
      return;
    }

    if (submissionType === 'image' && !imageFile) {
      setError('Por favor, selecione uma imagem da redação');
      return;
    }

    if (!title.trim()) {
      setError('Por favor, digite um título para a redação');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Simulate submission delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSuccess(true);
      
      // Reset form after success
      setTimeout(() => {
        setTitle('');
        setContent('');
        setSelectedTheme(null);
        setImageFile(null);
        setImagePreview(null);
        setSubmissionType('text');
        setSuccess(false);
      }, 3000);

    } catch (err) {
      setError('Erro na demonstração. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle analysis completion
  const handleAnalysisComplete = async (analysis) => {
    console.log('Demo analysis completed:', analysis);
  };

  return (
    <div className="enem-training-page">
      <div className="page-header">
        <div className="demo-banner">
          <h1>🎯 Demo - Treinamento ENEM Redação</h1>
          <p>Teste o sistema de correção de redações ENEM com IA sem precisar fazer login. Suas redações não serão salvas.</p>
        </div>
      </div>

      <div className="main-content">
        <div className="essay-form-section">
          <h2>✍️ Nova Redação (Demo)</h2>
          
          {/* Theme Selection */}
          <div className="theme-selection">
            <h3>Escolha um Tema</h3>
            <div className="themes-grid">
              {themes.map(theme => (
                <div 
                  key={theme.id}
                  className={`theme-card ${selectedTheme?.id === theme.id ? 'selected' : ''}`}
                  onClick={() => handleThemeSelect(theme)}
                >
                  <div className="theme-year">{theme.year}</div>
                  <div className="theme-title">{theme.title}</div>
                  <div className="theme-description">{theme.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Submission Type Selection */}
          <div className="submission-type">
            <h3>Tipo de Envio</h3>
            <div className="type-options">
              <label className={`type-option ${submissionType === 'text' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="text"
                  checked={submissionType === 'text'}
                  onChange={(e) => setSubmissionType(e.target.value)}
                />
                <span className="option-icon">💻</span>
                <span className="option-label">Escrever no Editor</span>
              </label>
              <label className={`type-option ${submissionType === 'image' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="image"
                  checked={submissionType === 'image'}
                  onChange={(e) => setSubmissionType(e.target.value)}
                />
                <span className="option-icon">📷</span>
                <span className="option-label">Enviar Foto da Redação</span>
              </label>
            </div>
          </div>

          {/* Essay Form */}
          <form onSubmit={handleSubmit} className="essay-form">
            <div className="form-group">
              <label htmlFor="title">Título da Redação</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Digite o título da sua redação..."
                required
              />
            </div>

            {submissionType === 'text' ? (
              <div className="form-group">
                <div className="content-header">
                  <label htmlFor="content">Conteúdo da Redação</label>
                  {inlineErrors.length > 0 && (
                    <div className="markup-controls">
                      <label className="markup-toggle">
                        <input
                          type="checkbox"
                          checked={showMarkups}
                          onChange={(e) => setShowMarkups(e.target.checked)}
                        />
                        Mostrar marcações inline ({inlineErrors.length})
                      </label>
                    </div>
                  )}
                </div>
                <InlineMarkupEditor
                  content={content}
                  onChange={handleContentChange}
                  inlineErrors={inlineErrors}
                  showMarkups={showMarkups}
                  placeholder="Escreva sua redação aqui para testar a correção com IA..."
                />
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="image">Foto da Redação</label>
                <div className="image-upload">
                  <input
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={handleImageUpload}
                    required
                  />
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview da redação" />
                    </div>
                  )}
                </div>
                <div className="upload-tips">
                  <p>📝 Dicas para uma boa foto:</p>
                  <ul>
                    <li>Certifique-se de que o texto está legível</li>
                    <li>Use boa iluminação</li>
                    <li>Mantenha a câmera estável</li>
                    <li>Inclua toda a redação na foto</li>
                  </ul>
                </div>
              </div>
            )}

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                Redação enviada com sucesso no modo demo! Agora você pode analisá-la com IA. (Não salva no modo demo)
              </div>
            )}

          </form>
        </div>

        {/* AI Analysis Panel */}
        {(content || imageFile) && (
          <DemoAIAnalysisPanel
            content={submissionType === 'text' ? content : imagePreview}
            onAnalysisComplete={handleAnalysisComplete}
            onInlineErrorsUpdate={handleInlineErrorsUpdate}
            isImageAnalysis={submissionType === 'image'}
          />
        )}
      </div>

      {/* Demo Recent Essays */}
      <div className="recent-essays">
        <h2>📚 Redações de Exemplo (Demo)</h2>
        <p>Estas são redações de exemplo para demonstrar como o sistema funciona:</p>
        <div className="essays-grid">
          {demoEssays.map(essay => (
            <div 
              key={essay.id} 
              className="essay-card demo-card"
            >
              <div className="essay-header">
                <h3>{essay.title}</h3>
                <div className="essay-date">{essay.formattedDate}</div>
              </div>
              <div className="essay-meta">
                <span className="essay-type">
                  {essay.type === 'image' ? '📷' : '💻'} 
                  {essay.type === 'image' ? 'Imagem' : 'Texto'}
                </span>
                <span className="word-count">{essay.wordCount} palavras</span>
              </div>
              {essay.hasAnalysis && (
                <div className="essay-score">
                  <span 
                    className="score-badge"
                    style={{ backgroundColor: essay.scoreColor }}
                  >
                    {essay.analysis.totalScore}/1000
                  </span>
                </div>
              )}
              <div className="essay-status">
                {essay.hasAnalysis ? '✅ Analisada (Demo)' : '⏳ Aguardando análise (Demo)'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DemoENEMTrainingPage;
